// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Build mode: the state machine, and the one owner of what a hand is for (M13-08, 06 §9).
//
// `XRRuntime` already has five things that read the hands every frame — the cabinet rig's press
// gates, the plunger grab, the nudge detector, `CabinetGrab`'s corner carry and the placement
// ritual's "reaching for the machine commits it" — and Build mode adds a sixth and a seventh: the
// tray's taps and the part carry. Seven readers of one pair of hands is seven owners, and this
// repository has recorded what two owners of one frame cost (*"two writers on opposite sides of
// one frame are two owners, and the later one wins silently"*). So there is one arbiter. Every
// frame it says, per hand, what that hand is *for*, and it says which of the existing readers may
// run at all. `rig.advance`, `CabinetGrab`, `startPlaying` and the seated offer are consulted only
// when this hands the frame over; while a machine is being edited, a hand within `CORNER_REACH`
// of a front corner post is reaching for a part, not lifting the cabinet.
//
// **Build mode never moves the machine** (06 §9's standing rule, XR-002c). The bench and the flip
// are the two placements it asks for — `testFlip.ts` derives both — and the cabinet's own carry
// is off for as long as the mode is on, because Back to build has to restore the bench to the
// centimetre and a cabinet the author had dragged aside during a test would make that restore a
// *move*.
//
// **The draft is a value held here, outside the scene graph** (standing rule 6): a session
// recycle (XR-030) rebuilds every mesh and this object survives it, as a ball in play does.
//
// Pure: no Three.js, no DOM, no clock. Hands arrive already transformed into the playfield's
// frame and already matched to plinths by the tray; what leaves is a draft, a ghost to draw, and
// the arbiter's answer.

import {devicePosition,moveDevice} from '../../builder/parts'
import type { TestPlayVerdict } from '../../builder/playable'
import {   validateMachine, type MachineDefinition, type Point2, type WireLayer } from '../../../core/ts/machine'
import {targetBackingForEditing} from '../../builder/targetBacking'
import { anchorAt, canRedo, canUndo, devicesOf, editDevice, editLayer, layersOf, openDraftForEditing, redo, redoIds, resetTo, select, selectLayer, undo, undoIds, type LayoutDraft } from '../../builder/layoutDraft'
import type { Placement } from '../placement'
import type { LevelDirection } from '../viewerRay'
import { PartCarry, type CarryFrame, type CarryHand, type CarryOptions, type CarrySource } from './partCarry'
import { ruleChanged, snapViewOf, type SnapView } from './snapView'
import { calloutKey, validationViewOf, type Callout, type ValidationView } from './validationView'
import { CalloutTaps } from './calloutTaps'
import { InspectorInput, inspectorRowsOf, inspectorViewOf, round, wireCardAt, type InspectorFrame, type InspectorRow, type InspectorView } from './inspector'
import { MiniMapInput, miniMapViewOf, planCatchMetres, type MiniMapView, type NudgeArrow } from './miniMap'
import { NO_SHAPE_EVENTS, RampShaper, type PointsView, type ShapeEvents } from './rampPoints'
import { fieldsFor, toStored, writeAngle, writeChoice, writeNumber, type DeviceField, type Editable } from '../../builder/fields'
import { benchStringsFor, benchText, type BenchLanguage, type BenchStrings } from './benchStrings'

/** The view for a document the geometry pass could not read: nothing drawn, nothing counted. */
const UNREADABLE: ValidationView = { map: undefined, callouts: [], measures: [], errors: 0, warnings: 0, key: 'unreadable' }
import { reportFor } from '../../builder/validation'
import {
  TestFlip,
  benchPlacementFor,
  fullPlacementFor,
  type BenchCabinet,
  type FlipDecision,
  type FlipRoom,
  type HeadPose,
} from './testFlip'

/**
 * Where the mode is.
 *
 * `entering` and `leaving` each last exactly as long as the host needs to rebuild the picture —
 * shrink the machine onto the bench and raise the tray, or the reverse — and neither reads a
 * hand: a pinch on the frame the tray is appearing is a pinch on nothing.
 */
export type BuildPhase =
  | 'idle'
  | 'entering'
  | 'editing'
  | 'carrying'
  | 'placing'
  | 'inspecting'
  /** A ramp's control point is in a hand (M13-24). */
  | 'shaping'
  | 'testing'
  | 'leaving'

/** What one hand is for this frame. */
export type HandRole =
  /** The game's: flippers, plunger, nudge, and the cabinet's own carry. */
  | 'play'
  /** The tray's and the board's: tapping tiles, pinching parts. */
  | 'build'
  /** Holding a part. */
  | 'carry'
  /** Nothing: the mode is between pictures, or the session is hidden. */
  | 'none'

/** Which of `XRRuntime`'s readers may run this frame, and what each hand is for. */
export interface Arbiter {
  readonly phase: BuildPhase
  /** May `rig.advance` read the hands — flippers, plunger, nudges, the corner carry? */
  readonly rigRuns: boolean
  /** May `CabinetGrab` take the cabinet by a corner? Never while the mode is on. */
  readonly cabinetGrabRuns: boolean
  /** May a confirmation end the placement ritual (`startPlaying`)? Never while the mode is on. */
  readonly startPlayingRuns: boolean
  /** May the seated detector offer comfort mode? Never while the mode is on — it moves the machine. */
  readonly seatedOfferRuns: boolean
  /** May the tray take taps and the carry take pinches? */
  readonly buildRuns: boolean
  readonly left: HandRole
  readonly right: HandRole
}

const OFF: Arbiter = {
  phase: 'idle',
  rigRuns: true,
  cabinetGrabRuns: true,
  startPlayingRuns: true,
  seatedOfferRuns: true,
  buildRuns: false,
  left: 'play',
  right: 'play',
}

/** One thing the card wrote, or refused to (M13-22): for the host's toast and its say line. */
export interface InspectorEdit {
  /** The part's id, or the wire's for a wire's card. */
  readonly deviceId: string
  readonly path: string
  /** The row's label — *Skirt radius*. */
  readonly label: string
  /** A stepper tapped, or a rail slid to a value. */
  readonly kind: 'step' | 'slide'
  /** The value as the row now shows it — *21 mm*, *Left* — or the refusal. */
  readonly text: string
  /** The validator's sentence when the write was not made, as the carry's `refused`. */
  readonly refused: string | undefined
}

/** A tap on the mini-map's arrow (M13-23): the part moved, by how much, or why not. */
export interface Nudged {
  readonly deviceId: string
  readonly dx: number
  readonly dy: number
  readonly mm: number
  /** The validator's sentence when the move was not made, as the carry's `refused`. */
  readonly refused: string | undefined
}

/** A tap on the map's plan (tap-to-select): where it landed, and the part it chose, if any. */
export interface MapPicked {
  /** Playfield metres: the point drawn under the fingertip. */
  readonly at: Point2
  /** The part whose handle is nearest within the plan's catch — now selected — or nothing: nothing changed. */
  readonly deviceId: string | undefined
}

/** What the host draws and says after a frame of Build mode. */
export interface BuildFrame {
  readonly arbiter: Arbiter
  /** The carry's own report, when the carry ran this frame. */
  readonly carry: CarryFrame | undefined
  /** True when the definition changed this frame and the picture should be patched. */
  readonly changed: boolean
  /** What the snap looks like this frame — the guides, the reticle, the label — or nothing (M13-13). */
  readonly guides: SnapView | undefined
  /** True on the frame the snap's rule changed into something caught: the click, the tick, the flash. */
  readonly ruleChanged: boolean
  /** The report on the machine — the decal, the callouts, the measurements — or nothing outside editing (M13-17). */
  readonly validation: ValidationView | undefined
  /** The card beside the selected part — its rows, where it floats — or nothing: no selection, a part in hand (M13-22). */
  readonly inspector: InspectorView | undefined
  /** What the card wrote this frame, one per tap or slide value, refusals included (M13-22). */
  readonly edits: readonly InspectorEdit[]
  /** The mini-map beside the bench — the plan, the pads, where it floats — or nothing outside editing (M13-23). */
  readonly map: MiniMapView | undefined
  /** The arrow tapped this frame and what it did (M13-23). */
  readonly nudged: Nudged | undefined
  /** A tap on the plan this frame and the part it chose (tap-to-select). */
  readonly picked: MapPicked | undefined
  /** The selected ramp's control points — the handles, the one in hand, the curve as it is now — or nothing (M13-24). */
  readonly points: PointsView | undefined
  /** What the hands did to a ramp this frame: chose one, took a point, let it go, were refused (M13-24). */
  readonly shaped: ShapeEvents
}

const NO_EDITS: readonly InspectorEdit[] = []

/** A row's next value for one tap: a step along its rail, the next of its options round, the bottom of the rail for a dash. */
/** The draft's wire of that id, or nothing — a ramp, a plane, or no layer at all is not one. */
function wireOf(draft: LayoutDraft, layerId: string): WireLayer | undefined {
  const layer = layersOf(draft).find((l) => l.id === layerId)
  return layer?.kind === 'wire' ? layer : undefined
}

function stepped(row: InspectorRow, direction: -1 | 1): number {
  if (row.kind === 'choice') {
    const n = row.options.length
    return n === 0 ? 0 : ((((row.value ?? 0) + direction) % n) + n) % n
  }
  if (row.value === undefined) return row.min
  if (row.path === 'holdMs' && row.value < 0) return direction > 0 ? 0 : row.min
  return Math.min(row.max, Math.max(row.min, round(row.value + direction * row.step, row.step)))
}

/** A shown value — author units, degrees, an option's index — written through the Builder's own `write*`. */
function writeShown<T extends Editable>(device: T, field: DeviceField, shown: number): T {
  switch (field.type) {
    case 'number':
      return writeNumber(device, field, toStored(shown, field.unit))
    case 'angle':
      return writeAngle(device, field, shown)
    case 'choice': {
      const option = field.options[shown]
      return option === undefined ? device : writeChoice(device, field, option.value)
    }
  }
}

export class BuildMode {
  private phaseNow: BuildPhase = 'idle'
  private draftNow: LayoutDraft | undefined
  /** The machine Build began on: what Reset puts back (the 1.0.115 round). */
  private originalNow: MachineDefinition | undefined
  private benchNow: Placement | undefined
  private symmetryOn = true
  /** The language the bench speaks (M13-26): the host sets it from the player's choice. */
  private languageNow: BenchLanguage = 'en'
  /** Last frame's guides, so a rule change is a change (M13-13). */
  private guidesNow: SnapView | undefined
  /** The overlay for the definition it was read off, so a frame that changed nothing computes nothing (M13-17). */
  private validationNow: { readonly definition: MachineDefinition; readonly strings: BenchStrings; readonly view: ValidationView } | undefined
  readonly carry = new PartCarry()
  /** The fingertips through the callouts (M13-17). */
  readonly callouts = new CalloutTaps()
  /** The card's steppers and rails (M13-22). */
  readonly inspector = new InspectorInput()
  /** The card for the definition, selection and scale it was read off, so a frame that changed nothing computes nothing (M13-22). */
  private inspectorNow:
    | {
        readonly definition: MachineDefinition
        /** The part's id, or `wire:` and the wire's. */
        readonly selected: string
        readonly scale: number
        readonly strings: BenchStrings
        readonly view: InspectorView | undefined
      }
    | undefined
  /** The fingertips through the mini-map's arrows (M13-23). */
  readonly nudges = new MiniMapInput()
  /** How many definitions the draft has been through: the plan's repaint key (M13-23). */
  private mapVersion = 0
  private mapDefinition: MachineDefinition | undefined
  /** The fingertips on the selected ramp's control points (M13-24). */
  readonly shaper = new RampShaper()
  /** How many slides so far: each is its own gesture, so each is its own undo (M13-22). */
  private slides = 0
  private slideGesture: string | undefined
  /**
   * A slide (M13-22) or a point in hand (M13-24) changed the definition and the host has not
   * adopted it: it does at the release, not thirty times a second.
   */
  private adoptPending = false
  readonly flip = new TestFlip()

  get phase(): BuildPhase {
    return this.phaseNow
  }

  /** Is the mode on at all — anything but idle? */
  get active(): boolean {
    return this.phaseNow !== 'idle'
  }

  get draft(): LayoutDraft | undefined {
    return this.draftNow
  }

  /** The bench placement the author is at, or undefined outside the mode. */
  get bench(): Placement | undefined {
    return this.benchNow
  }

  get symmetry(): boolean {
    return this.symmetryOn
  }

  /** The language every word the bench makes is in (M13-26). */
  get language(): BenchLanguage {
    return this.languageNow
  }

  set language(language: BenchLanguage) {
    this.languageNow = language
  }

  /** The bench's words in that language. */
  get strings(): BenchStrings {
    return benchStringsFor(this.languageNow)
  }

  /**
   * Begin: the draft opens on the machine as it stands, and the bench is derived from the head.
   *
   * `idle → entering`. The host shrinks the machine onto the returned placement, raises the tray,
   * and calls `entered`. Refused (returns `undefined`) from any other phase.
   */
  enter(
    definition: MachineDefinition,
    head: HeadPose,
    cabinet: BenchCabinet,
    eyeHeight?: number,
    toward?: LevelDirection,
  ): Placement | undefined {
    if (this.phaseNow !== 'idle') return undefined
    this.draftNow = openDraftForEditing(definition)
    this.originalNow = this.draftNow.definition
    // Along `toward` when the shell gives one — the direction of the machine the bench replaces.
    this.benchNow = benchPlacementFor(head, cabinet, eyeHeight ?? head.y, toward)
    this.phaseNow = 'entering'
    return this.benchNow
  }

  /**
   * The tracking space was renumbered under the author — a session recycle — so the bench is
   * derived again from the head the new space reports (XR-030; "a world coordinate is not a
   * place"). Answers the placement to apply now: the new bench, or, mid-test at full size, the
   * Full placement from the same head. Nothing outside the mode.
   */
  rebench(head: HeadPose, cabinet: BenchCabinet, eyeHeight?: number): Placement | undefined {
    if (!this.active) return undefined
    const bench = benchPlacementFor(head, cabinet, eyeHeight ?? head.y)
    this.benchNow = bench
    this.flip.rebench(bench)
    if (this.phaseNow === 'testing' && this.flip.outcome === 'full') return fullPlacementFor(head, cabinet)
    return bench
  }

  /** The picture is on the bench and the tray is up. `entering → editing`. */
  entered(): void {
    if (this.phaseNow === 'entering') this.phaseNow = 'editing'
  }

  /**
   * One frame of hands while editing.
   *
   * Runs the inspector, then the carry, and moves between `editing`, `carrying`, `placing` and
   * `inspecting`. In every other phase it does nothing to the draft and answers only the arbiter
   * — a hand in `testing` is the game's. `hands` are pinch points; `tips` are fingertips, for the
   * card's steppers (M13-22) — a host that gives none has a card that is slid and not stepped.
   */
  advance(hands: readonly CarryHand[], options: Omit<CarryOptions, 'symmetry'>, tips: readonly CarryHand[] = []): BuildFrame {
    const draft = this.draftNow
    if (draft === undefined || !this.editingNow()) {
      this.guidesNow = undefined
      return {
        arbiter: this.arbiter(),
        carry: undefined,
        changed: false,
        guides: undefined,
        ruleChanged: false,
        validation: undefined,
        inspector: undefined,
        edits: NO_EDITS,
        map: undefined,
        nudged: undefined,
        picked: undefined,
        points: undefined,
        shaped: NO_SHAPE_EVENTS,
      }
    }
    // The card first (M13-22), so a hand over it is the card's before the carry can read a pinch
    // on a rail as a pinch on the part under it.
    const inspected = this.inspect(draft, hands, tips, options.playfield, options.scale)
    // The mini-map's arrows next (M13-23), on the side away from the card; a tap moves the selected
    // part by exactly its millimetres, and a hand over the map is the map's too.
    const mapBefore = this.miniMapViewOf(inspected.draft, options.playfield, options.scale)
    const mapFrame = this.nudges.advance(mapBefore, tips, options.scale, this.carry.carrying, options.frameMs ?? 0)
    const nudge = mapFrame.nudged === undefined ? undefined : this.nudge(inspected.draft, mapFrame.nudged)
    const nudgedDraft = nudge === undefined ? inspected.draft : nudge.draft
    // A tap on the plan chooses the part drawn under the fingertip: the nearest handle within
    // half a target on the plan, the Builder's own `anchorAt` and `select`. A tap on nothing
    // changes nothing — the map is not a way to put a part down by accident.
    const pick = mapFrame.picked === undefined || mapBefore === undefined ? undefined : this.pick(nudgedDraft, mapFrame.picked, mapBefore)
    const beforeShape = pick === undefined ? nudgedDraft : pick.draft
    // The selected ramp's points next (M13-24): a pinch on one takes it before the carry can read
    // the pinch as a pinch on the part under it, a pinch on a ramp's curve chooses the ramp, and
    // the hand that did either is the ramp's until it opens.
    const shaped = this.shaper.advance(
      beforeShape,
      hands.map((hand) => (inspected.over[hand.handedness] || mapFrame.over[hand.handedness] ? { ...hand, grab: false } : hand)),
      { board: options.board, scale: options.scale, symmetry: this.symmetryOn, strings: this.strings },
      this.carry.carrying,
    )
    const masked = hands.map((hand) =>
      inspected.over[hand.handedness] || mapFrame.over[hand.handedness] || shaped.over[hand.handedness] ? { ...hand, grab: false } : hand,
    )
    const frame = this.carry.advance(shaped.draft, masked, { ...options, symmetry: this.symmetryOn, strings: this.strings })
    // A slide, or a point in hand, changes the definition on every frame the value moves, and the
    // host adopts once, at the release: an adoption rebuilds the session, and thirty rebuilds a
    // second is not a slider. The card and the shaped curve follow live — they are drawn from
    // the draft, not from the session.
    const sliding = this.inspector.holding !== undefined
    const shaping = shaped.shaping
    const live = sliding || shaping
    if (live && frame.draft.definition !== draft.definition) this.adoptPending = true
    const changed = live ? false : frame.draft.definition !== draft.definition || this.adoptPending
    if (!live) this.adoptPending = false
    this.draftNow = frame.draft
    this.phaseNow = frame.dropped
      ? frame.committed
        ? 'placing'
        : 'editing'
      : frame.held !== undefined
        ? 'carrying'
        : sliding
          ? 'inspecting'
          : shaping
            ? 'shaping'
            : 'editing'
    const arbiter = this.arbiter(frame)
    // `placing` is the one frame the host patches the picture on; it is over by the next.
    if (this.phaseNow === 'placing') this.phaseNow = 'editing'
    // The guides (M13-13): read off the snap against the devices the snap was made against —
    // every one but the part in hand — so a partner it names is always there to draw a line to.
    const moving = frame.source?.kind === 'moved' ? frame.source.deviceId : undefined
    const guides = snapViewOf(
      frame.snap,
      frame.raw,
      moving === undefined ? devicesOf(frame.draft) : devicesOf(frame.draft).filter((device) => device.id !== moving),
      options.playfield,
      this.strings,
    )
    const rule = ruleChanged(this.guidesNow, guides)
    this.guidesNow = guides
    return {
      arbiter,
      carry: frame,
      changed,
      guides,
      ruleChanged: rule,
      validation: this.validationOf(frame.draft),
      // No card while a part is in hand: the selected part is the one being moved, and a card
      // beside where it was is a card beside nothing.
      // Nor while a wire's point is in hand: the card is beside the wire's middle, which moves with it.
      inspector: frame.held === undefined && !shaping ? this.inspectorViewOf(frame.draft, options.playfield, options.scale) : undefined,
      edits: inspected.edits,
      map: this.miniMapViewOf(frame.draft, options.playfield, options.scale),
      nudged: nudge?.nudged,
      picked: pick?.picked,
      points: frame.held === undefined ? shaped.view : undefined,
      shaped: {
        chose: shaped.chose,
        took: shaped.took,
        dropped: shaped.dropped,
        refused: shaped.refused,
        added: shaped.added,
        leaving: shaped.leaving,
        removed: shaped.removed,
      },
    }
  }

  /**
   * The mini-map for this draft (M13-23): beside the playfield, away from the card, its plan
   * keyed by how many definitions the draft has been through, its arrows live while a part is
   * selected and none is in hand.
   */
  private miniMapViewOf(draft: LayoutDraft, playfield: CarryOptions['playfield'], scale: number): MiniMapView {
    if (draft.definition !== this.mapDefinition) {
      this.mapDefinition = draft.definition
      this.mapVersion += 1
    }
    const card = this.inspectorViewOf(draft, playfield, scale)
    return miniMapViewOf(
      playfield,
      scale,
      { devices: devicesOf(draft), layers: draft.definition.layers, selected: draft.selected, selectedLayer: draft.selectedLayer },
      this.mapVersion,
      card?.at[0],
      draft.selected !== undefined && !this.carry.carrying,
    )
  }

  /**
   * One arrow's move on the selected part, under `nudge:<id>` so a run of taps is one undo —
   * and refused, as a release is, when the validator would refuse the machine it makes.
   */
  private pick(draft: LayoutDraft, at: Point2, view: MiniMapView): { readonly draft: LayoutDraft; readonly picked: MapPicked } {
    const hit = anchorAt(devicesOf(draft), at, planCatchMetres(view))
    if (hit === undefined) return { draft, picked: { at, deviceId: undefined } }
    return { draft: select(draft, hit.deviceId), picked: { at, deviceId: hit.deviceId } }
  }

  private nudge(draft: LayoutDraft, arrow: NudgeArrow): { readonly draft: LayoutDraft; readonly nudged: Nudged } | undefined {
    const selected = draft.selected
    if (selected === undefined) return undefined
    const next = editDevice(draft, selected, (device) => moveDevice(device, arrow.dx, arrow.dy), `nudge:${selected}`)
    if (next === draft) return undefined
    const first = validateMachine(next.definition).errors[0]
    if (first !== undefined) {
      return { draft, nudged: { deviceId: selected, dx: arrow.dx, dy: arrow.dy, mm: arrow.mm, refused: benchText(this.strings, 'toast.notPlaced', { message: first.message }) } }
    }
    return { draft: next, nudged: { deviceId: selected, dx: arrow.dx, dy: arrow.dy, mm: arrow.mm, refused: undefined } }
  }

  /**
   * The card for the selected part (M13-22), read once per definition, selection and scale.
   * Nothing with nothing selected, or for a part the draft no longer has.
   */
  private inspectorViewOf(draft: LayoutDraft, playfield: CarryOptions['playfield'], scale: number): InspectorView | undefined {
    // A part's card, or a chosen wire's (26 Sep 2026) — the draft holds one selection or the
    // other, never both (`selectLayer`).
    const layerId = draft.selectedLayer
    const selected = draft.selected ?? (layerId === undefined ? undefined : `wire:${layerId}`)
    if (selected === undefined) {
      this.inspectorNow = undefined
      return undefined
    }
    const cached = this.inspectorNow
    const strings = this.strings
    if (
      cached !== undefined
      && cached.definition === draft.definition
      && cached.selected === selected
      && cached.scale === scale
      && cached.strings === strings
    ) {
      return cached.view
    }
    let view: InspectorView | undefined
    if (draft.selected !== undefined) {
      const device = devicesOf(draft).find((d) => d.id === selected)
      const at = device === undefined ? undefined : devicePosition(device)
      view = device === undefined || at === undefined ? undefined : inspectorViewOf(device, at, playfield, scale, strings)
    } else {
      // A ramp has no card: its numbers are its shape, which the hand gives it.
      const layer = layerId === undefined ? undefined : wireOf(draft, layerId)
      view = layer === undefined ? undefined : inspectorViewOf(layer, wireCardAt(layer.spline), playfield, scale, strings)
    }
    this.inspectorNow = { definition: draft.definition, selected, scale, strings, view }
    return view
  }

  /**
   * The card's frame (M13-22): the steppers and rails read against this frame's card, and every
   * action written to the draft — a tap under `inspector:<id>:<path>`, so a run of taps is one
   * entry as a run of edits to one field is on the canvas; a slide under its own number, so every
   * slide is its own entry, as the Builder's own slider drag is.
   */
  private inspect(
    draft: LayoutDraft,
    hands: readonly CarryHand[],
    tips: readonly CarryHand[],
    playfield: CarryOptions['playfield'],
    scale: number,
  ): { readonly draft: LayoutDraft; readonly over: InspectorFrame['over']; readonly edits: readonly InspectorEdit[] } {
    const view = this.inspectorViewOf(draft, playfield, scale)
    // A point of the wire in hand is the shaper's, as a part in hand is the carry's: the card
    // takes no gesture while either is held, so a hand carrying a point over the card keeps it.
    const busy = this.carry.carrying || this.shaper.holding !== undefined
    const frame = this.inspector.advance(view, tips, hands, scale, busy)
    if (view === undefined || frame.actions.length === 0) return { draft, over: frame.over, edits: NO_EDITS }
    const wire = view.kind === 'wire'
    const edits: InspectorEdit[] = []
    let next = draft
    for (const action of frame.actions) {
      switch (action.kind) {
        case 'grip':
          this.slides += 1
          this.slideGesture = `inspector:${view.deviceId}:${action.path}:slide${this.slides}`
          break
        case 'release':
          this.slideGesture = undefined
          break
        case 'step': {
          const written = this.written(next, view.deviceId, wire, action.path, (row) => stepped(row, action.direction), `inspector:${view.deviceId}:${action.path}`, 'step')
          next = written.draft
          if (written.edit !== undefined) edits.push(written.edit)
          break
        }
        case 'slide': {
          const gesture = this.slideGesture ?? `inspector:${view.deviceId}:${action.path}:slide${this.slides}`
          const written = this.written(next, view.deviceId, wire, action.path, () => action.value, gesture, 'slide')
          next = written.draft
          if (written.edit !== undefined) edits.push(written.edit)
          break
        }
      }
    }
    return { draft: next, over: frame.over, edits }
  }

  /**
   * One value written to one row of the selected part, through the Builder's own `write*` —
   * clamped as they clamp — and refused, as a release is (`PartCarry`), when the validator would
   * refuse the machine it makes. Nothing for a value the row already shows.
   */
  private written(
    draft: LayoutDraft,
    deviceId: string,
    wire: boolean,
    path: string,
    value: (row: InspectorRow) => number,
    gesture: string,
    kind: InspectorEdit['kind'],
  ): { readonly draft: LayoutDraft; readonly edit: InspectorEdit | undefined } {
    const device: Editable | undefined = wire ? wireOf(draft, deviceId) : devicesOf(draft).find((d) => d.id === deviceId)
    if (device === undefined) return { draft, edit: undefined }
    const fields: readonly DeviceField[] | undefined = fieldsFor(device.kind)
    const field = (fields ?? []).find((f) => f.path === path)
    const strings = this.strings
    const row = inspectorRowsOf(device, strings).find((r) => r.path === path)
    if (field === undefined || row === undefined) return { draft, edit: undefined }
    const shown = value(row)
    if (shown === row.value && field.type !== 'choice') return { draft, edit: undefined }
    // A wire is written through the Builder's own `editLayer`, as its points are (`rampPoints.ts`).
    const next = wire
      ? editLayer(draft, deviceId, (layer) => (layer.kind === 'wire' ? writeShown(layer, field, shown) : layer), gesture)
      : editDevice(draft, deviceId, (d) => writeShown(d, field, shown), gesture)
    if (next === draft) return { draft, edit: undefined }
    const first = validateMachine(next.definition).errors[0]
    if (first !== undefined) {
      const refused = benchText(strings, 'toast.notSet', { message: first.message })
      return { draft, edit: { deviceId, path, label: row.label, kind, text: refused, refused } }
    }
    const after: Editable | undefined = wire ? wireOf(next, deviceId) : devicesOf(next).find((d) => d.id === deviceId)
    const text = after === undefined ? '' : (inspectorRowsOf(after, strings).find((r) => r.path === path)?.text ?? '')
    return { draft: next, edit: { deviceId, path, label: row.label, kind, text, refused: undefined } }
  }

  /**
   * The report on the machine as it stands (M13-17): the Builder's own `reportFor`, read into
   * what the bench draws, once per definition — a release, an undo, a redo — and never per frame.
   */
  get validation(): ValidationView | undefined {
    const draft = this.draftNow
    return draft === undefined || !this.editingNow() ? undefined : this.validationOf(draft)
  }

  private validationOf(draft: LayoutDraft): ValidationView {
    const cached = this.validationNow
    const strings = this.strings
    if (cached !== undefined && cached.definition === draft.definition && cached.strings === strings) return cached.view
    // The geometry pass expects a definition the loader has passed. A bench never opens on
    // anything else, but a frame callback must not throw on the day one does: the overlay for a
    // document the pass cannot read is nothing, not an exception (the native client's rule too).
    let view: ValidationView
    try {
      view = validationViewOf(draft.definition, reportFor(draft.definition), strings)
    } catch {
      view = UNREADABLE
    }
    this.validationNow = { definition: draft.definition, strings, view }
    return view
  }

  /**
   * A frame of fingertips over the callouts (M13-17). Answers the callout tapped this frame —
   * the part it names is selected, as the Builder's click on a complaint selects it — or nothing.
   * `hands` are fingertips in the playfield's frame; `scale` is the bench's.
   */
  tapCallout(hands: readonly CarryHand[], scale: number): Callout | undefined {
    const draft = this.draftNow
    if (draft === undefined || !this.editingNow()) {
      this.callouts.release()
      return undefined
    }
    const view = this.validationOf(draft)
    const spots = view.callouts.map((callout) => ({ key: calloutKey(callout), x: callout.at[0], y: callout.at[1], lift: callout.lift }))
    const key = this.callouts.advance(spots, hands, scale, this.carry.carrying)
    if (key === undefined) return undefined
    const callout = view.callouts.find((c) => calloutKey(c) === key)
    if (callout === undefined) return undefined
    if (callout.deviceId !== undefined) this.draftNow = select(draft, callout.deviceId)
    else if (callout.layerId !== undefined) this.draftNow = selectLayer(draft, callout.layerId)
    return callout
  }

  /** What may run, and what each hand is for. */
  arbiter(frame?: CarryFrame): Arbiter {
    const phase = this.phaseNow
    if (phase === 'idle') return OFF
    const held = frame?.held ?? (this.carry.carrying ? this.heldHand() : undefined)
    // Both hands carry while both hold a two-ended part (M13-14).
    const second = frame?.second ?? (this.carry.carrying ? this.carry.secondHand : undefined)
    const building = this.editingNow()
    const role = (hand: 'left' | 'right'): HandRole => {
      if (phase === 'testing') return 'play'
      if (!building) return 'none'
      return held === hand || second === hand ? 'carry' : 'build'
    }
    return {
      phase,
      rigRuns: phase === 'testing',
      cabinetGrabRuns: false,
      startPlayingRuns: false,
      seatedOfferRuns: false,
      buildRuns: building,
      left: role('left'),
      right: role('right'),
    }
  }

  /** Undo / redo, from the Build page. True when anything changed. */
  /**
   * Put the selection down: no device, no layer, so no card (26 Sep 2026). A tray tile tapped while
   * a part's card is up asks for the plinths, and the plinths and the card share the space over the
   * near playfield, so the card gives way. False when nothing was selected.
   */
  deselect(): boolean {
    const draft = this.draftNow
    if (draft === undefined || (draft.selected === undefined && draft.selectedLayer === undefined)) return false
    this.draftNow = selectLayer(select(draft, undefined), undefined)
    return true
  }

  undo(): boolean {
    const draft = this.draftNow
    if (draft === undefined || !this.editingNow() || !canUndo(draft)) return false
    this.dropTheSlide()
    this.draftNow = undo(this.carry.cancel(draft))
    this.phaseNow = 'editing'
    return true
  }

  redo(): boolean {
    const draft = this.draftNow
    if (draft === undefined || !this.editingNow() || !canRedo(draft)) return false
    this.dropTheSlide()
    this.draftNow = redo(this.carry.cancel(draft))
    this.phaseNow = 'editing'
    return true
  }

  get canUndo(): boolean {
    return this.draftNow !== undefined && canUndo(this.draftNow)
  }

  /**
   * The machine put back as it was when Build began, as one step Undo takes back (the 1.0.115
   * round). False — nothing done — when the draft already is that machine, or outside editing.
   */
  reset(): boolean {
    const draft = this.draftNow
    const original = this.originalNow
    if (draft === undefined || original === undefined || !this.editingNow() || !this.canReset) return false
    this.dropTheSlide()
    this.draftNow = resetTo(this.carry.cancel(draft), targetBackingForEditing(original))
    this.phaseNow = 'editing'
    return true
  }

  /** The draft is not the machine Build began on. */
  get canReset(): boolean {
    return this.draftNow !== undefined && this.originalNow !== undefined && this.draftNow.definition !== this.originalNow
  }

  /** What the next Undo would take back, in the bench's words — "flipper1", "3 parts" — or undefined. */
  get undoWhat(): string | undefined {
    return this.draftNow === undefined || !canUndo(this.draftNow) ? undefined : whatOf(undoIds(this.draftNow), this.strings)
  }

  /** What the next Redo would put back, in the bench's words, or undefined. */
  get redoWhat(): string | undefined {
    return this.draftNow === undefined || !canRedo(this.draftNow) ? undefined : whatOf(redoIds(this.draftNow), this.strings)
  }

  get canRedo(): boolean {
    return this.draftNow !== undefined && canRedo(this.draftNow)
  }

  /** The Symmetry row. Returns the new state. */
  toggleSymmetry(): boolean {
    this.symmetryOn = !this.symmetryOn
    return this.symmetryOn
  }

  /**
   * Test play: the verdict, and where the machine goes for it.
   *
   * `editing → testing` only when the verdict is playable; a refusal leaves the author exactly
   * where they were, with the verdict's sentence to read. A carry in flight is cancelled first —
   * a part in the hand when the machine grows is a part nobody placed.
   */
  beginTest(head: HeadPose, cabinet: BenchCabinet, room: FlipRoom, prepared?: TestPlayVerdict): FlipDecision | undefined {
    const draft = this.draftNow
    const bench = this.benchNow
    if (draft === undefined || bench === undefined || !this.editingNow()) return undefined
    this.dropTheSlide()
    this.draftNow = this.carry.cancel(draft)
    const decision = this.flip.begin(this.draftNow.definition, bench, head, cabinet, room, this.strings, prepared)
    if (decision.outcome !== 'refused') this.phaseNow = 'testing'
    return decision
  }

  /** Back to build: the bench, as the author left it. `testing → editing`. */
  endTest(): Placement | undefined {
    if (this.phaseNow !== 'testing') return undefined
    const bench = this.flip.end()
    this.phaseNow = 'editing'
    return bench
  }

  /**
   * Leave: whatever phase it is in, the mode is over. `* → leaving`.
   *
   * The draft is kept until `left`, so the host can still read what to save; the carry is
   * cancelled, never committed, for the reason `PartCarry.cancel` gives.
   */
  leave(): void {
    if (this.phaseNow === 'idle') return
    this.dropTheSlide()
    if (this.draftNow !== undefined) this.draftNow = this.carry.cancel(this.draftNow)
    if (this.flip.testing) this.flip.end()
    this.phaseNow = 'leaving'
  }

  /** The picture is back where it was. `leaving → idle`. */
  left(): void {
    if (this.phaseNow !== 'leaving') return
    this.phaseNow = 'idle'
    this.draftNow = undefined
    this.originalNow = undefined
    this.benchNow = undefined
    this.inspectorNow = undefined
  }

  /**
   * The input method changed, or the session recycled: let go of a part without placing it.
   *
   * The draft survives — that is the point of holding it here — and the phase falls back to
   * `editing` if it was `carrying`.
   */
  cancelGrabs(): CarrySource | undefined {
    const source = this.carry.source
    if (this.draftNow !== undefined) this.draftNow = this.shaper.cancel(this.carry.cancel(this.draftNow))
    if (this.phaseNow === 'carrying' || this.phaseNow === 'placing' || this.phaseNow === 'inspecting' || this.phaseNow === 'shaping') this.phaseNow = 'editing'
    this.guidesNow = undefined
    this.callouts.release()
    this.nudges.release()
    // The slide is let go of, but what it wrote stands, and the host adopts it on the next frame.
    this.inspector.release()
    this.slideGesture = undefined
    return source
  }

  /** A slide in flight is over, and what it wrote is the host's business no longer: undo, redo, a test, leaving. */
  private dropTheSlide(): void {
    this.inspector.release()
    this.nudges.release()
    this.shaper.release()
    this.slideGesture = undefined
    this.adoptPending = false
  }

  private editingNow(): boolean {
    const p = this.phaseNow
    return p === 'editing' || p === 'carrying' || p === 'placing' || p === 'inspecting' || p === 'shaping'
  }

  private heldHand(): 'left' | 'right' | undefined {
    return this.carry.heldBy
  }
}

/**
 * The parts a history step changes, in the bench's words (the 1.0.115 round): the one part's id,
 * "3 parts", or "a change" for a step that changed nothing a part or a layer holds.
 * `BuildMode.WhatOf` on the headset.
 */
export function whatOf(ids: readonly string[], strings: BenchStrings): string {
  if (ids.length === 0) return strings['what.change']
  if (ids.length === 1) return ids[0] ?? strings['what.change']
  return benchText(strings, 'what.parts', { n: String(ids.length) })
}
